import React, { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Search,
  Filter,
  Download,
  Eye,
  Ticket,
  TrendingUp,
  TrendingDown,
  Calendar,
  ChevronLeft,
  ChevronRight,
  MoreHorizontal,
  User,
  Home,
  Clock,
} from 'lucide-react';
import {
  Card,
  Button,
  Input,
  Select,
  Badge,
  Skeleton,
  cn,
  formatNaira,
  StatCard,
  Tabs,
} from '@raffle/ui';

interface TicketSale {
  id: string;
  ticketNumbers: string[];
  user: {
    id: string;
    name: string;
    email: string;
    avatar?: string;
  };
  property: {
    id: string;
    title: string;
    image: string;
  };
  quantity: number;
  totalAmount: number;
  purchaseDate: Date;
  status: 'confirmed' | 'pending' | 'refunded';
  paymentMethod: 'card' | 'bank_transfer' | 'wallet';
}

const mockTickets: TicketSale[] = [
  {
    id: 'TKT-001',
    ticketNumbers: ['00145', '00146', '00147'],
    user: { id: 'u1', name: 'Adebayo Johnson', email: 'adebayo@email.com' },
    property: { id: 'p1', title: 'Luxury Villa in Lekki Phase 1', image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=100' },
    quantity: 3,
    totalAmount: 3000,
    purchaseDate: new Date(Date.now() - 1000 * 60 * 30),
    status: 'confirmed',
    paymentMethod: 'card',
  },
  {
    id: 'TKT-002',
    ticketNumbers: ['00341'],
    user: { id: 'u2', name: 'Ngozi Okonkwo', email: 'ngozi@email.com' },
    property: { id: 'p2', title: 'Modern Apartment in Victoria Island', image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=100' },
    quantity: 1,
    totalAmount: 1000,
    purchaseDate: new Date(Date.now() - 1000 * 60 * 60 * 2),
    status: 'confirmed',
    paymentMethod: 'wallet',
  },
  {
    id: 'TKT-003',
    ticketNumbers: ['00567', '00568', '00569', '00570', '00571'],
    user: { id: 'u3', name: 'Chinedu Eze', email: 'chinedu@email.com' },
    property: { id: 'p1', title: 'Luxury Villa in Lekki Phase 1', image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=100' },
    quantity: 5,
    totalAmount: 5000,
    purchaseDate: new Date(Date.now() - 1000 * 60 * 60 * 5),
    status: 'pending',
    paymentMethod: 'bank_transfer',
  },
  {
    id: 'TKT-004',
    ticketNumbers: ['00089', '00090'],
    user: { id: 'u4', name: 'Folake Adeyemi', email: 'folake@email.com' },
    property: { id: 'p3', title: 'Penthouse in Ikoyi', image: 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=100' },
    quantity: 2,
    totalAmount: 2000,
    purchaseDate: new Date(Date.now() - 1000 * 60 * 60 * 24),
    status: 'confirmed',
    paymentMethod: 'card',
  },
  {
    id: 'TKT-005',
    ticketNumbers: ['00234'],
    user: { id: 'u5', name: 'Emeka Nwosu', email: 'emeka@email.com' },
    property: { id: 'p1', title: 'Luxury Villa in Lekki Phase 1', image: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=100' },
    quantity: 1,
    totalAmount: 1000,
    purchaseDate: new Date(Date.now() - 1000 * 60 * 60 * 48),
    status: 'refunded',
    paymentMethod: 'card',
  },
];

const statusColors = {
  confirmed: 'success',
  pending: 'warning',
  refunded: 'error',
} as const;

const paymentMethodLabels = {
  card: 'Card',
  bank_transfer: 'Bank Transfer',
  wallet: 'Wallet',
};

export const TicketsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateRange, setDateRange] = useState('7days');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState<TicketSale | null>(null);

  const filteredTickets = mockTickets.filter((ticket) => {
    const matchesSearch =
      ticket.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      ticket.property.title.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'all' || ticket.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const formatDate = (date: Date) => {
    const now = new Date();
    const diff = now.getTime() - date.getTime();
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  const exportToCSV = () => {
    const headers = ['ID', 'User', 'Property', 'Quantity', 'Amount', 'Status', 'Date'];
    const rows = filteredTickets.map((t) => [
      t.id,
      t.user.name,
      t.property.title,
      t.quantity,
      t.totalAmount,
      t.status,
      t.purchaseDate.toISOString(),
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `tickets-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Stats
  const totalSales = mockTickets.reduce((sum, t) => sum + (t.status === 'confirmed' ? t.totalAmount : 0), 0);
  const ticketsSold = mockTickets.reduce((sum, t) => sum + (t.status === 'confirmed' ? t.quantity : 0), 0);
  const pendingAmount = mockTickets.reduce((sum, t) => sum + (t.status === 'pending' ? t.totalAmount : 0), 0);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-bold text-text-primary">Ticket Sales</h1>
          <p className="text-text-secondary">Monitor and manage all ticket purchases</p>
        </div>
        <Button variant="outline" onClick={exportToCSV}>
          <Download className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
      </div>

      {/* Stats */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Sales"
          value={formatNaira(totalSales)}
          change={{ value: 12.5, isPositive: true }}
          icon={<TrendingUp className="w-5 h-5" />}
        />
        <StatCard
          title="Tickets Sold"
          value={ticketsSold.toLocaleString()}
          change={{ value: 8.2, isPositive: true }}
          icon={<Ticket className="w-5 h-5" />}
        />
        <StatCard
          title="Pending Amount"
          value={formatNaira(pendingAmount)}
          icon={<Clock className="w-5 h-5" />}
        />
        <StatCard
          title="Avg. Order Value"
          value={formatNaira(totalSales / mockTickets.filter((t) => t.status === 'confirmed').length || 0)}
          change={{ value: 3.1, isPositive: true }}
          icon={<TrendingUp className="w-5 h-5" />}
        />
      </div>

      {/* Filters */}
      <Card className="p-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-muted" />
            <input
              type="text"
              placeholder="Search by ID, user, or property..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-10 pl-10 pr-4 bg-surface-2 border border-white/10 rounded-vault text-text-primary placeholder:text-text-muted focus:outline-none focus:border-gold-500/50"
            />
          </div>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: 'all', label: 'All Status' },
              { value: 'confirmed', label: 'Confirmed' },
              { value: 'pending', label: 'Pending' },
              { value: 'refunded', label: 'Refunded' },
            ]}
            className="w-full sm:w-40"
          />
          <Select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            options={[
              { value: '7days', label: 'Last 7 days' },
              { value: '30days', label: 'Last 30 days' },
              { value: '90days', label: 'Last 90 days' },
              { value: 'all', label: 'All time' },
            ]}
            className="w-full sm:w-40"
          />
        </div>
      </Card>

      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/5">
                <th className="px-6 py-4 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Order
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Customer
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Property
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Tickets
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-4 text-left text-xs font-medium text-text-muted uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-4 text-right text-xs font-medium text-text-muted uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {filteredTickets.map((ticket) => (
                <motion.tr
                  key={ticket.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-surface-2/50 transition-colors"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-mono text-sm text-gold-500">{ticket.id}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gold-500/10 flex items-center justify-center">
                        <User className="w-4 h-4 text-gold-500" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-text-primary">{ticket.user.name}</p>
                        <p className="text-xs text-text-muted">{ticket.user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={ticket.property.image}
                        alt=""
                        className="w-10 h-10 rounded-vault object-cover"
                      />
                      <span className="text-sm text-text-primary max-w-[200px] truncate">
                        {ticket.property.title}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-2">
                      <Ticket className="w-4 h-4 text-text-muted" />
                      <span className="text-sm font-medium text-text-primary">{ticket.quantity}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="font-mono text-sm font-medium text-text-primary">
                      {formatNaira(ticket.totalAmount)}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <Badge variant={statusColors[ticket.status]} className="capitalize">
                      {ticket.status}
                    </Badge>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className="text-sm text-text-muted">{formatDate(ticket.purchaseDate)}</span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <button
                      onClick={() => setSelectedTicket(ticket)}
                      className="p-2 rounded-vault text-text-muted hover:text-text-primary hover:bg-surface-3 transition-colors"
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-white/5">
          <p className="text-sm text-text-muted">
            Showing <span className="font-medium text-text-primary">1</span> to{' '}
            <span className="font-medium text-text-primary">{filteredTickets.length}</span> of{' '}
            <span className="font-medium text-text-primary">{filteredTickets.length}</span> results
          </p>
          <div className="flex gap-2">
            <button
              className="p-2 rounded-vault bg-surface-2 text-text-muted hover:text-text-primary disabled:opacity-50"
              disabled
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button className="px-3 py-2 rounded-vault bg-gold-500 text-black text-sm font-medium">
              1
            </button>
            <button
              className="p-2 rounded-vault bg-surface-2 text-text-muted hover:text-text-primary disabled:opacity-50"
              disabled
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </Card>

      {/* Detail Modal */}
      {selectedTicket && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80"
          onClick={() => setSelectedTicket(null)}
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="w-full max-w-lg bg-surface-1 rounded-vault-lg border border-white/10 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-6 border-b border-white/10">
              <h2 className="font-display text-lg font-semibold text-text-primary">
                Order Details
              </h2>
              <p className="text-sm text-text-muted">Order {selectedTicket.id}</p>
            </div>
            <div className="p-6 space-y-6">
              {/* Customer */}
              <div>
                <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
                  Customer
                </p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-gold-500/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-gold-500" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-text-primary">{selectedTicket.user.name}</p>
                    <p className="text-xs text-text-muted">{selectedTicket.user.email}</p>
                  </div>
                </div>
              </div>

              {/* Property */}
              <div>
                <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
                  Property
                </p>
                <div className="flex items-center gap-3">
                  <img
                    src={selectedTicket.property.image}
                    alt=""
                    className="w-16 h-12 rounded-vault object-cover"
                  />
                  <p className="text-sm text-text-primary">{selectedTicket.property.title}</p>
                </div>
              </div>

              {/* Ticket Numbers */}
              <div>
                <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-2">
                  Ticket Numbers
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedTicket.ticketNumbers.map((num) => (
                    <span
                      key={num}
                      className="px-3 py-1.5 bg-gold-500/10 border border-gold-500/20 rounded-vault font-mono text-sm text-gold-500"
                    >
                      #{num}
                    </span>
                  ))}
                </div>
              </div>

              {/* Payment Details */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-1">
                    Amount
                  </p>
                  <p className="font-mono text-lg font-bold text-gold-500">
                    {formatNaira(selectedTicket.totalAmount)}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-1">
                    Payment Method
                  </p>
                  <p className="text-sm text-text-primary">
                    {paymentMethodLabels[selectedTicket.paymentMethod]}
                  </p>
                </div>
                <div>
                  <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-1">
                    Status
                  </p>
                  <Badge variant={statusColors[selectedTicket.status]} className="capitalize">
                    {selectedTicket.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-1">
                    Purchase Date
                  </p>
                  <p className="text-sm text-text-primary">
                    {selectedTicket.purchaseDate.toLocaleDateString()}
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex gap-3">
              {selectedTicket.status === 'pending' && (
                <Button variant="primary" className="flex-1">
                  Confirm Payment
                </Button>
              )}
              {selectedTicket.status === 'confirmed' && (
                <Button variant="outline" className="flex-1">
                  Issue Refund
                </Button>
              )}
              <Button variant="outline" onClick={() => setSelectedTicket(null)}>
                Close
              </Button>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};
